/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Componente Organism para mostrar los detalles del cliente en el inbox.
 * Presenta información básica y personalizada del cliente, con opción de expandir/collapse y edición.
 * @module CustomerDetailMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactShowMoreText from "react-show-more-text";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ButtonComponent from "../../../components/ButtonMC";
import useCustomerDetailUI from "../../../hooks/inbox/ui/useCustomerDetailUI";
import useCustomerDetailLogic from "../../../hooks/inbox/forms/useCustomerDetailLogic";
import { getFlagIcon } from "../../../../services/countryService";
import FepBadgeMC from "./FepBadgeMC";

/**
 * CustomFieldItem - Componente para mostrar un campo personalizado en el detalle del cliente.
 */
const CustomFieldItem = memo(({ field, index, formatAnswer }) => (
  <div key={`customer-custom-field-view-${index}`} className="tw-mb-1">
    <span className="tw-text-xs tw-font-normal tw-text-blue-500">
      {`${field.name}:`}
    </span>
    <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal tw-truncate">
      {formatAnswer(field)}
    </span>
  </div>
));

CustomFieldItem.displayName = 'CustomFieldItem';

/**
 * Componente Organism para mostrar los detalles del cliente en el inbox.
 *
 * Atomic Design: Organism
 * - Compuesto por ButtonComponent, ReactShowMoreText y campos personalizados
 * - Permite expandir/collapse detalles y edición del cliente
 * - Utiliza TailwindCSS con .tw-wrapper
 * - Traducción internacionalizada con useTranslation
 * - Optimizado con memo, useCallback y useMemo
 */
const CustomerDetailMC = memo(({
  customerData,
  onEdit,
  customerFields,
  customFields,
  loadCustomerFieldData
}) => {
  const { t } = useTranslation('InboxMC');
  const {
    viewMore,
    viewToggleConfig,
    editButtonConfig,
    toggleButtonConfig
  } = useCustomerDetailUI();

  const {
    customFieldsGrouped,
    formatAnswer
  } = useCustomerDetailLogic(customerFields, customFields);

  const handleEditClick = useCallback(() => {
    onEdit?.();
  }, [onEdit]);

  const customerBasicInfo = useMemo(() => ({
    webId: customerData?.web_id || customerData?.webId || '',
    name: customerData?.fullName || '',
    phone: customerData?.visiblePhoneNumberValue || '',
    countryId: customerData?.country_id || '',
    email: customerData?.email || '',
    identification: customerData?.id_number || '',
    notes: customerData?.notes || ''
  }), [
    customerData?.web_id,
    customerData?.webId,
    customerData?.fullName,
    customerData?.visiblePhoneNumberValue,
    customerData?.country_id,
    customerData?.email,
    customerData?.id_number,
    customerData?.notes
  ]);

  const handleToggleClick = useCallback(() => {
    loadCustomerFieldData?.();
    viewToggleConfig.onClick();
  }, [loadCustomerFieldData, viewToggleConfig]);

  return (
    <div className="tw-wrapper">
      <div className="tw-flex tw-justify-between tw-items-center tw-flex-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-medium tw-text-gray-950">
            {t('customerDetails.title')}
          </span>
          <FepBadgeMC conversation={customerData} />
        </div>
        <ButtonComponent
          {...editButtonConfig}
          onClick={handleEditClick}
          data-tooltip-id="details-button-tooltip"
        />
        <ReactTooltip
          id="details-button-tooltip"
          className="tw-z-50"
        >
          <span>{t('customerDetails.editButtonTooltip')}</span>
        </ReactTooltip>
      </div>
      {customerBasicInfo.webId && (
        <div className="tw-mb-1">
          <span className="tw-text-xs tw-font-normal tw-text-blue-500">
            {t('customerDetails.id', 'ID:')}
          </span>
          <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal tw-truncate">
            {customerBasicInfo.webId}
          </span>
        </div>
      )}
      <div className="tw-mb-1">
        <span className="tw-text-xs tw-font-normal tw-text-blue-500">
          {t('customerDetails.name')}
        </span>
        <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal tw-truncate">
          {customerBasicInfo.name}
        </span>
      </div>
      <div className="tw-mb-1">
        <span className="tw-text-xs tw-font-normal tw-text-blue-500">
          {t('customerDetails.phone')}
        </span>
        <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal tw-truncate">
          {getFlagIcon(customerBasicInfo.countryId, 'tw-mr-1')}
          {customerBasicInfo.phone}
        </span>
      </div>
      <div className="tw-mb-1">
        <span className="tw-text-xs tw-font-normal tw-text-blue-500">
          {t('customerDetails.email')}
        </span>
        <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal tw-truncate">
          {customerBasicInfo.email}
        </span>
      </div>

      {viewMore && (
        <>
          <div className="tw-mb-1">
            <span className="tw-text-xs tw-font-normal tw-text-blue-500">
              {t('customerDetails.identification')}
            </span>
            <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal tw-truncate">
              {customerBasicInfo.identification}
            </span>
          </div>
          <div className="tw-mb-1">
            <span className="tw-text-xs tw-font-normal tw-text-blue-500">
              {t('customerDetails.notes')}
            </span>
            <span className="tw-ml-1 tw-text-default tw-text-gray-700 tw-font-normal">
              <ReactShowMoreText
                more={t('customerDetails.more')}
                less={t('customerDetails.less')}
                expanded={false}
                className="tw-break-words"
              >
                {customerBasicInfo.notes}
              </ReactShowMoreText>
            </span>
          </div>

          {customFieldsGrouped.map((field, index) => (
            <CustomFieldItem
              key={`${field.id}-${index}`}
              field={field}
              index={index}
              formatAnswer={formatAnswer}
            />
          ))}
        </>
      )}

      <div className="tw-flex tw-justify-center tw-mt-1">
        <ButtonComponent
          {...toggleButtonConfig}
          text={t(viewToggleConfig.text)}
          icon={viewToggleConfig.icon}
          onClick={handleToggleClick}
        />
      </div>
    </div>
  );
});

CustomerDetailMC.displayName = 'CustomerDetailMC';

export default CustomerDetailMC;
